import React, { useCallback, useMemo } from 'react';
import { TypedMemo } from '../../../../common/types';
import { LoadingView } from '../../../../common/views/LoadingView';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { useGetReleasePlanQuery } from '../../../../../redux/api/fymAPI';
import { ReleaseTabProps } from '../releaseTabProps';
import { ClosedRolloutLine } from './ClosedRolloutLine';
import { NoDatePanel } from './NoDatePanel';
import { PlanChannelChips } from './PlanChannelChips';
import { localDay, planMode } from './planMode';
import { PushPlanPanel } from './PushPlanPanel';
import { ReleasePostsPanel } from './ReleasePostsPanel';
import { RolloutRunSheet } from './rollout/RolloutRunSheet';
import { browserTimeZone, wallTimeOf } from './rollout/rolloutTime';
import { usePlanChannelPick } from './usePlanChannelPick';
import { useReleasePlanChannels } from './useReleasePlanChannels';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Plan tab; each part uses its own slice of it */
import styles from './Plan.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The Release plan tab of a release (tab key `plan`). An upcoming song, or a song released up to 7
 * days ago, gets the rollout run sheet (design rollout-plan a1–e1, `rollout/RolloutRunSheet`). An
 * older release gets "Push this song" (one post a week for 6 weeks) and its closed rollout line. A
 * song with no date gets the prompt to set the date in the Catalog. The AI reels campaign of the
 * song has its own tab (`campaign/CampaignTab`). Every tab shows the posts of the release. Every
 * post here also shows in the Calendar.
 */
const PlanTabComponent: React.FC<ReleaseTabProps> = ({ accountId, item, release }) => {
  const channels = useReleasePlanChannels(accountId);
  const { selected, toggle } = usePlanChannelPick(channels.platforms);
  const mode = useMemo(() => planMode(item.releaseDate, new Date()), [item.releaseDate]);
  const timeZone = useMemo(() => browserTimeZone(), []);
  const kind = mode === 'push' ? 'evergreen' : 'release';
  // The run sheet (design rollout-plan) posts each step on its own channels: it reads every usable
  // channel. "Push this song" keeps its "Post on" chips.
  const plan = useGetReleasePlanQuery(
    {
      accountId,
      catalogItemUuid: item.uuid,
      kind,
      platforms: mode === 'push' ? selected : channels.platforms,
      timeZone,
    },
    { skip: mode === 'noDate' || channels.isLoading }
  );
  const artistZone = plan.data?.timeZone ?? null;
  const today = useMemo(
    () => (artistZone === null ? localDay(new Date()) : wallTimeOf(new Date().toISOString(), artistZone).slice(0, 10)),
    [artistZone]
  );
  const { refetch } = plan;
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const chips = <PlanChannelChips platforms={channels.platforms} selected={selected} onToggle={toggle} />;

  let main: React.ReactNode;
  if (mode === 'noDate') {
    main = <NoDatePanel catalogItemUuid={item.uuid} />;
  } else if (channels.isLoading || plan.isLoading) {
    main = <LoadingView />;
  } else if (channels.error !== undefined) {
    main = <ErrorView error={channels.error} retry={channels.retry} />;
  } else if (plan.data === undefined) {
    main = <ErrorView error={plan.error} retry={retry} />;
  } else if (mode === 'push') {
    main = (
      <>
        <PushPlanPanel
          accountId={accountId}
          catalogItemUuid={item.uuid}
          plan={plan.data}
          platforms={selected}
          channelChips={chips}
          today={today}
        />
        {item.releaseDate !== null && (
          <ClosedRolloutLine
            accountId={accountId}
            catalogItemUuid={item.uuid}
            releaseDate={item.releaseDate.slice(0, 10)}
            today={today}
          />
        )}
      </>
    );
  } else {
    main = (
      <RolloutRunSheet
        accountId={accountId}
        item={item}
        release={release}
        plan={plan.data}
        channels={channels.channels}
        today={today}
      />
    );
  }

  return (
    <div className={styles.plan} data-tour="plan">
      {main}
      {mode !== 'release' && <ReleasePostsPanel accountId={accountId} catalogItemUuid={item.uuid} title={item.title} />}
    </div>
  );
};

export const PlanTab = TypedMemo(PlanTabComponent);
